
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Package, Calendar, Star } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/context/LanguageContext';
import { useIsMobile } from '@/hooks/use-mobile';
import MainLayout from '@/components/layout/MainLayout';

interface Redemption {
  id: string;
  reward_id: string;
  points_spent: number;
  status: 'pending' | 'processing' | 'completed' | 'cancelled';
  created_at: string;
  rewards?: {
    name: string;
    description?: string;
    category: string;
    image_url?: string;
  };
}

// Define the raw database response type
interface RedemptionFromDB {
  id: string;
  reward_id: string;
  points_spent: number;
  status: string; // Raw string from database
  created_at: string;
  rewards?: {
    name: string;
    description?: string;
    category: string;
    image_url?: string;
  };
}

const RedemptionHistory = () => {
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();
  const { language } = useLanguage();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (user) {
      fetchRedemptions();
    }
  }, [user]);

  const fetchRedemptions = async () => {
    try {
      setIsLoading(true);
      
      const { data: redemptionsData, error } = await supabase
        .from('reward_redemptions')
        .select(`
          *,
          rewards:reward_id (
            name,
            description,
            category,
            image_url
          )
        `)
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Type conversion to ensure status is properly typed
      const typedRedemptions: Redemption[] = (redemptionsData as RedemptionFromDB[] || []).map(redemption => ({
        ...redemption,
        status: ['pending', 'processing', 'completed', 'cancelled'].includes(redemption.status) 
          ? redemption.status as 'pending' | 'processing' | 'completed' | 'cancelled'
          : 'pending'
      }));

      setRedemptions(typedRedemptions);
    } catch (error) {
      console.error('Error fetching redemptions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return language === 'en' ? 'Pending' : '待處理';
      case 'processing':
        return language === 'en' ? 'Processing' : '處理中';
      case 'completed':
        return language === 'en' ? 'Completed' : '已完成';
      case 'cancelled':
        return language === 'en' ? 'Cancelled' : '已取消';
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            <span className="ml-2 text-gray-600">
              {language === 'en' ? 'Loading...' : '載入中...'}
            </span>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {language === 'en' ? 'Redemption History' : '兌換紀錄'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {language === 'en' ? 'View your reward redemption history' : '查看您的獎勵兌換紀錄'}
          </p>
        </div>

        {redemptions.length === 0 ? (
          <Card>
            <CardContent className="p-12">
              <div className="text-center">
                <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {language === 'en' ? 'No Redemptions Yet' : '暫無兌換紀錄'}
                </h3>
                <p className="text-gray-600">
                  {language === 'en' 
                    ? 'You haven\'t redeemed any rewards yet.' 
                    : '您還沒有兌換過任何獎勵。'}
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {redemptions.map((redemption) => (
              <Card key={redemption.id}>
                <CardContent className="p-6">
                  <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-4`}>
                    {redemption.rewards?.image_url && (
                      <div className="flex-shrink-0">
                        <img
                          src={redemption.rewards.image_url}
                          alt={redemption.rewards.name}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold text-lg">
                          {redemption.rewards?.name || '未知商品'}
                        </h3>
                        <Badge className={getStatusColor(redemption.status)}>
                          {getStatusText(redemption.status)}
                        </Badge>
                      </div>
                      <p className="text-gray-600 mb-3">
                        {redemption.rewards?.description}
                      </p>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <Star className="h-4 w-4" />
                          <span>
                            {language === 'en' ? 'Points Spent:' : '消耗積分:'} {redemption.points_spent}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          <span>
                            {language === 'en' ? 'Redeemed:' : '兌換時間:'} {' '}
                            {new Date(redemption.created_at).toLocaleDateString('zh-TW')}
                          </span>
                        </div>
                        <div>
                          <span>
                            {language === 'en' ? 'Category:' : '類別:'} {redemption.rewards?.category}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default RedemptionHistory;
